import { montoBonito } from "../../../../utils/monto";
import { UPDATE_TYPE } from "../../../../models/EnrollmentUpdateDTO";
import horarioBonito from "../../../../utils/horarioBonito";
export { errorText } from "../enrollmentRequests/helpers";

/** Nombre de horario como se lee en pantalla: "Sábados 12:00 pm – 3:30 pm" (el nombre guardado viene en 24 horas y en singular). */
export const nombreHorario = (nombre: string | null | undefined): string => horarioBonito(nombre, { plural: true, separador: " " });

/** Honduras bills in lempiras; the other countries in pesos/dollars with "$". */
export const dinero = (valor: any, countryId?: number | null): string => (countryId === 2 ? "L " : "$") + montoBonito(valor).replace(/\.00$/, "");

/** Types the "Qué cambia" chips offer, in the order Académico thinks about them. */
export const TYPE_ORDER: number[] = [
	UPDATE_TYPE.SCHEDULE_CHANGE, UPDATE_TYPE.NEXT_BLOCK, UPDATE_TYPE.COURSE_CHANGE, UPDATE_TYPE.POSTPONED, UPDATE_TYPE.NO_SCHEDULE,
	UPDATE_TYPE.REENTRY, UPDATE_TYPE.RETIRED, UPDATE_TYPE.NOT_STARTED, UPDATE_TYPE.ERROR, UPDATE_TYPE.GRADUATED
];

export const TYPE_LABELS: { [id: number]: string } = {
	1: "Cambio de Horario", 2: "Siguiente Bloque", 3: "Cambio a Otro Curso", 4: "Aplazar", 5: "Sin Horario",
	6: "Reingreso", 7: "Retiro", 8: "No Comenzó", 9: "Error", 10: "Graduada"
};

/** Chip id for "Cambio de Vencimiento": it is not an update type, it goes through the due-day endpoint. */
export const CHIP_VENCIMIENTO = 0;

/** Types that need a destination schedule. */
export const needsSchedule = (typeId: number | null | undefined): boolean =>
	typeId === UPDATE_TYPE.SCHEDULE_CHANGE || typeId === UPDATE_TYPE.NEXT_BLOCK || typeId === UPDATE_TYPE.REENTRY || typeId === UPDATE_TYPE.COURSE_CHANGE;

/** Sessions ahead/behind when moving to a schedule that has already given `given` classes. */
export const sessionDelta = (taken: number, given: number): { less: number; more: number } =>
	({ less: Math.max(0, given - taken), more: Math.max(0, taken - given) });

/*
	Estatus de matrícula 5 se quedó con su id, pero cuando la cerró un cambio a otro curso (la fila trae
	newEnrollmentId) se lee "Cambio de curso" (ESPEC FASE 2, 2026-09-30).
*/
export const enrollmentStatusLabel = (statusId: number | null | undefined, label: string | null | undefined, newEnrollmentId?: number | null): string => {
	if (statusId === 5 && newEnrollmentId) { return "Cambio de Curso"; }
	if (statusId === 9 && !label) { return "Sin Horario"; }
	return label || "";
};

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2 oct" o, con hora, "2 oct, 12:09 am". */
export const fechaCorta = (iso: string | null | undefined): string => {
	if (!iso) { return ""; }
	// The API answers in UTC ("2026-10-02 02:26"); dates-only are calendar days and stay as they are.
	const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? "" : "Z"));
	if (isNaN(d.getTime())) { return iso; }
	const base = `${d.getDate()} ${MESES[d.getMonth()]}`;
	if (iso.length <= 10) { return base; }
	const h = d.getHours();
	return `${base}, ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export type Preset = "hoy" | "semana" | "mes" | "todo";
export const rangoPreset = (p: Preset): { from?: string; to?: string } => {
	const hoy = new Date();
	if (p === "hoy") { return { from: iso(hoy), to: iso(hoy) }; }
	if (p === "semana") { const l = new Date(hoy); l.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7)); return { from: iso(l), to: iso(hoy) }; }
	if (p === "mes") { return { from: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), to: iso(hoy) }; }
	return {};
};

/** What every tab receives from the controller. */
export interface TabProps {
	campusId: number;
	countryId?: number | null;
	movil: boolean;
	/** Su rol tiene el permiso de aplicar de la sección (Actualizar): sin él, todo queda en Por Aplicar. */
	canApplyNow: boolean;
	notify: (text: string, ok?: boolean) => void;
	/** Reload tab counters after something changed. */
	changed: () => void;
}

/** Mensualidad N de M y el día en que vence, leídos de la matrícula. */
export const planLinea = (tuitions: { typeId: number; tuitionNumber: number; statusId: number; progressStatusId: number | null; dueDate: string | null }[]): { n: number; m: number; dia: number | null } | null => {
	const mensuales = tuitions.filter((t) => t.typeId === 1 && t.progressStatusId !== 3).sort((a, b) => a.tuitionNumber - b.tuitionNumber);
	if (mensuales.length === 0) { return null; }
	const siguiente = mensuales.find((t) => t.statusId !== 2) || mensuales[mensuales.length - 1];
	const dia = siguiente.dueDate ? Number(siguiente.dueDate.slice(8, 10)) || null : null;
	// Posición dentro de las mensualidades: tuition_number cuenta también la inscripción (la 1).
	return { n: mensuales.indexOf(siguiente) + 1, m: mensuales.length, dia };
};
